<template>
  <el-dialog
    title="合并分类"
    :visible.sync="dialogVisible"
    width="1000px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
    top="5vh"
  >
    <!-- 步骤条 -->
    <el-steps :active="currentStep" finish-status="success" simple style="margin-bottom: 20px">
      <el-step title="选择组织"></el-step>
      <el-step title="解决冲突"></el-step>
      <el-step title="确认合并"></el-step>
    </el-steps>

    <!-- 步骤1：选择组织 -->
    <div v-show="currentStep === 0" class="step-content">
      <el-form label-width="140px">
        <el-form-item label="源销售组织">
          <el-select v-model="sourceSaleOrgId" placeholder="请选择源销售组织（被合并方）" filterable style="width: 350px">
            <el-option
              v-for="org in saleOrgList"
              :key="org.id"
              :label="org.name"
              :value="org.id"
            />
          </el-select>
          <div class="form-tip">源组织的分类将被合并到目标组织</div>
        </el-form-item>
        <el-form-item label="目标销售组织">
          <el-select v-model="targetSaleOrgId" placeholder="请选择目标销售组织（合并到）" filterable style="width: 350px">
            <el-option
              v-for="org in saleOrgList"
              :key="org.id"
              :label="org.name"
              :value="org.id"
            />
          </el-select>
          <div class="form-tip">合并后，目标组织将包含两个组织的所有分类</div>
        </el-form-item>
      </el-form>
      <el-alert
        title="合并说明"
        type="info"
        :closable="false"
        show-icon
      >
        <template slot="default">
          <ul class="alert-list">
            <li>合并操作会将源组织的分类体系合并到目标组织</li>
            <li>如果存在冲突（同名不同编码或同编码不同名），需要手动选择保留哪一方</li>
            <li>合并完成后，源组织的分类体系保持不变</li>
          </ul>
        </template>
      </el-alert>
    </div>

    <!-- 步骤2：解决冲突 -->
    <div v-show="currentStep === 1" class="step-content">
      <div v-if="conflicts.length > 0">
        <el-alert
          :title="`检测到 ${conflicts.length} 个冲突，请逐一选择保留哪一方的分类`"
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 15px"
        />
        <el-table :data="conflicts" border stripe max-height="400px">
          <el-table-column prop="conflictType" label="冲突类型" width="150">
            <template slot-scope="scope">
              <el-tag :type="scope.row.conflictType === 'SAME_NUMBER_DIFF_NAME' ? 'warning' : 'danger'" size="small">
                {{ getConflictTypeName(scope.row.conflictType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="源组织分类" min-width="180">
            <template slot-scope="scope">
              <div class="conflict-cell">
                <div>{{ scope.row.sourceNode.name }}</div>
                <div class="conflict-number">编码：{{ scope.row.sourceNode.number }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="目标组织分类" min-width="180">
            <template slot-scope="scope">
              <div class="conflict-cell">
                <div>{{ scope.row.targetNode.name }}</div>
                <div class="conflict-number">编码：{{ scope.row.targetNode.number }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="选择保留" width="200">
            <template slot-scope="scope">
              <el-radio-group v-model="conflictResolutions[scope.row.sourceNode.number]" size="small">
                <el-radio-button label="source">源组织</el-radio-button>
                <el-radio-button label="target">目标组织</el-radio-button>
              </el-radio-group>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-else>
        <el-result icon="success" title="无冲突" sub-title="两个组织的分类没有冲突，可以直接合并">
        </el-result>
      </div>
    </div>

    <!-- 步骤3：确认合并 -->
    <div v-show="currentStep === 2" class="step-content">
      <el-result icon="warning" title="确认合并">
        <template slot="subTitle">
          <div class="confirm-info">
            <p>源组织：<strong>{{ sourceSaleOrgName }}</strong></p>
            <p>目标组织：<strong>{{ targetSaleOrgName }}</strong></p>
            <p v-if="conflicts.length > 0">已解决冲突：<strong>{{ resolvedCount }} / {{ conflicts.length }}</strong></p>
          </div>
        </template>
      </el-result>
      <el-alert
        title="请确认以上信息无误后点击【确认合并】按钮"
        type="warning"
        :closable="false"
        show-icon
        center
      />
    </div>

    <div slot="footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button v-if="currentStep > 0" @click="prevStep">上一步</el-button>
      <el-button v-if="currentStep < 2" type="primary" @click="nextStep" :loading="checkLoading">
        {{ currentStep === 0 ? '检测冲突' : '下一步' }}
      </el-button>
      <el-button v-if="currentStep === 2" type="primary" @click="handleMerge" :loading="mergeLoading">
        确认合并
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { compareSaleCategory, mergeSaleCategory } from '@/api/system/sale_category'

export default {
  name: 'CategoryMerge',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    saleOrgList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentStep: 0,
      sourceSaleOrgId: '',
      targetSaleOrgId: '',
      conflicts: [],
      conflictResolutions: {},
      checkLoading: false,
      mergeLoading: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    sourceSaleOrgName() {
      const org = this.saleOrgList.find(o => o.id === this.sourceSaleOrgId)
      return org ? org.name : ''
    },
    targetSaleOrgName() {
      const org = this.saleOrgList.find(o => o.id === this.targetSaleOrgId)
      return org ? org.name : ''
    },
    resolvedCount() {
      return Object.keys(this.conflictResolutions).filter(key => this.conflictResolutions[key]).length
    },
    allConflictsResolved() {
      if (this.conflicts.length === 0) return true
      return this.resolvedCount === this.conflicts.length
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetData()
      }
    }
  },
  methods: {
    resetData() {
      this.currentStep = 0
      this.sourceSaleOrgId = ''
      this.targetSaleOrgId = ''
      this.conflicts = []
      this.conflictResolutions = {}
    },

    async nextStep() {
      if (this.currentStep === 0) {
        // 验证选择
        if (!this.sourceSaleOrgId) {
          this.$message.warning('请选择源销售组织')
          return
        }
        if (!this.targetSaleOrgId) {
          this.$message.warning('请选择目标销售组织')
          return
        }
        if (this.sourceSaleOrgId === this.targetSaleOrgId) {
          this.$message.warning('源组织和目标组织不能相同')
          return
        }

        // 检测冲突
        await this.checkConflicts()
      } else if (this.currentStep === 1) {
        // 验证冲突是否都已解决
        if (!this.allConflictsResolved) {
          this.$message.warning('请解决所有冲突后再继续')
          return
        }
        this.currentStep++
      }
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },

    async checkConflicts() {
      this.checkLoading = true
      try {
        const res = await compareSaleCategory({
          sourceSaleOrgId: this.sourceSaleOrgId,
          targetSaleOrgId: this.targetSaleOrgId
        })
        if (res.code === 200 && res.data) {
          // 提取冲突列表
          this.conflicts = res.data.conflicts || []
          // 初始化冲突解决方案，使用 sourceNode.number 作为 key
          // 注意：必须先构建完整对象再赋值，确保 Vue 2 响应式生效
          const resolutions = {}
          this.conflicts.forEach(c => {
            resolutions[c.sourceNode.number] = ''
          })
          this.conflictResolutions = resolutions
          this.currentStep++
        } else {
          this.$message.error(res.msg || '检测冲突失败')
        }
      } catch (error) {
        console.error('检测冲突失败:', error)
        this.$message.error('检测冲突失败')
      } finally {
        this.checkLoading = false
      }
    },

    async handleMerge() {
      this.mergeLoading = true
      try {
        // 合并接口参数：sourceSaleOrgId, targetSaleOrgId, conflictResolutions
        const mergeData = {
          sourceSaleOrgId: this.sourceSaleOrgId,
          targetSaleOrgId: this.targetSaleOrgId,
          conflictResolutions: this.conflictResolutions
        }

        const res = await mergeSaleCategory(mergeData)
        if (res.code === 200) {
          this.$message.success('合并成功')
          this.$emit('success')
          this.handleClose()
        } else {
          this.$message.error(res.msg || '合并失败')
        }
      } catch (error) {
        console.error('合并分类失败:', error)
        this.$message.error('合并分类失败')
      } finally {
        this.mergeLoading = false
      }
    },

    getConflictTypeName(type) {
      const nameMap = {
        'SAME_NUMBER_DIFF_NAME': '同编码不同名',
        'SAME_NAME_DIFF_NUMBER': '同名不同编码'
      }
      return nameMap[type] || type
    },

    handleClose() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.step-content {
  min-height: 300px;
  padding: 20px 0;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.alert-list {
  margin: 0;
  padding-left: 20px;
  line-height: 1.8;
}

.conflict-cell {
  line-height: 1.6;
}

.conflict-number {
  font-size: 12px;
  color: #909399;
}

.confirm-info {
  text-align: left;
  line-height: 2;
}

.confirm-info strong {
  color: #409eff;
}
</style>
